/**
 * OS PASSOS DO TOUR — o roteiro e a regra de quem vê o quê.
 *
 * QA99 item 5, "não oferecer ação sem permissão do papel": quem não gerencia
 * equipe não recebe o trecho engrenagem → convite enviado. O tour nunca aponta
 * um botão que o motor recusaria. O resto do roteiro é igual para todos.
 */
import { useUIStore } from '@/stores/uiStore'
import { focarCampoDaMensagem, garantirConversaAberta } from './preparar'
import type { PassoDoTour } from './types'

export type Traduzir = (chave: string) => string

export interface OpcoesDosPassos {
  /** Papel owner/admin ou permissão `team:read` (mesma regra de `SettingsLinks`). */
  podeGerirEquipe: boolean
  /** O botão do microfone existe na tela (some sem a chave de voz ou sem reconhecedor). */
  temMicrofone: boolean
  /** Limpa o `/` digitado pelo passo da barra. */
  limparCampo: () => void
}

const MEDIA_CELULAR = '(max-width: 767px)'

/** No celular a barra lateral é uma gaveta fechada: abre antes de apontar para dentro dela. */
export function abrirGavetaNoCelular(): void {
  if (window.matchMedia(MEDIA_CELULAR).matches) useUIStore.getState().setSidebarOpen(true)
}

/** Passos que apontam para o campo de mensagem: quem está no Hub ganha uma conversa aberta antes. */
const comConversa = async (): Promise<void> => {
  await garantirConversaAberta()
}

const TABELA = '[data-testid="team-settings-page"] [role="table"]'
const LINK_EQUIPE = 'a[href="/settings/team"]'
const MODAL = '[role="dialog"][aria-labelledby="invite-modal-title"]'

function passosDeEquipe(
  tx: (id: string, campo: 'titulo' | 'texto') => string,
  voltar: string,
): PassoDoTour[] {
  const base = (id: string) => ({ id, titulo: tx(id, 'titulo'), texto: tx(id, 'texto') })
  return [
    {
      ...base('engrenagem'),
      alvo: '[data-testid="sidebar-settings-btn"]',
      lado: 'right',
      prepara: abrirGavetaNoCelular,
      avanco: { tipo: 'clique', esperar: LINK_EQUIPE },
    },
    {
      ...base('equipe'),
      alvo: LINK_EQUIPE,
      lado: 'right',
      avanco: { tipo: 'clique', esperar: TABELA },
    },
    {
      ...base('listaDoTime'),
      alvo: TABELA,
      rota: 'equipe',
      lado: 'top',
      avanco: { tipo: 'botao' },
    },
    {
      ...base('convidar'),
      alvo: '[data-testid="open-invite-modal"]',
      rota: 'equipe',
      lado: 'bottom',
      alinhamento: 'end',
      avanco: { tipo: 'clique', esperar: '[data-testid="invite-email-input"]' },
    },
    {
      ...base('modalConvite'),
      alvo: MODAL,
      lado: 'left',
      avanco: { tipo: 'some', seletor: MODAL },
    },
    {
      ...base('conviteEnviado'),
      alvo: '[data-testid="team-settings-page"] > header + *',
      rota: 'equipe',
      lado: 'top',
      rotuloProximo: voltar,
      avanco: { tipo: 'botao' },
    },
  ]
}

export function montarPassos(t: Traduzir, o: OpcoesDosPassos): PassoDoTour[] {
  const tx = (id: string, campo: 'titulo' | 'texto') => t(`guidedTour.passos.${id}.${campo}`)
  const base = (id: string) => ({ id, titulo: tx(id, 'titulo'), texto: tx(id, 'texto') })
  const equipe = o.podeGerirEquipe ? passosDeEquipe(tx, t('guidedTour.botoes.voltarAoChat')) : []
  const microfone: PassoDoTour[] = o.temMicrofone
    ? [
        {
          ...base('microfone'),
          alvo: '[data-testid="mic-btn"]',
          rota: 'chat',
          prepara: comConversa,
          lado: 'top',
          avanco: { tipo: 'clique' },
        },
      ]
    : []
  return [
    {
      ...base('boasVindas'),
      destaque: true,
      rotuloProximo: t('guidedTour.botoes.comecar'),
      avanco: { tipo: 'botao' },
    },
    ...equipe,
    ...microfone,
    {
      ...base('barra'),
      alvo: '[data-testid="chat-input"]',
      rota: 'chat',
      prepara: comConversa,
      // "Digite / no campo": o campo já vem com o foco, quem digita logo vê a lista.
      aoMostrar: focarCampoDaMensagem,
      lado: 'top',
      avanco: { tipo: 'aparece', seletor: '[role="listbox"]' },
    },
    {
      ...base('listaDeComandos'),
      alvo: '[role="listbox"]',
      lado: 'top',
      avanco: { tipo: 'botao' },
      aoAvancar: o.limparCampo,
    },
    {
      ...base('indicadores'),
      alvo: '[data-testid="indicadores-btn"]',
      prepara: comConversa,
      lado: 'top',
      avanco: { tipo: 'botao' },
    },
    {
      ...base('maisAcoes'),
      alvo: '[data-testid="quick-actions-trigger"]',
      prepara: comConversa,
      lado: 'top',
      avanco: { tipo: 'botao' },
    },
    {
      ...base('novaConversa'),
      alvo: '[data-testid="new-conversation-btn"]',
      lado: 'right',
      prepara: abrirGavetaNoCelular,
      avanco: { tipo: 'botao' },
    },
    { ...base('fim'), destaque: true, avanco: { tipo: 'botao' } },
  ]
}
